import { Icon } from "@iconify/react";
import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useQueries } from "react-query";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ExcelExportParams,
  GridApi,
  SideBarDef,
} from "ag-grid-community";
import { Tooltip as MuiTooltip } from "@mui/material";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  Error,
  HStack,
  Notes,
  VStack,
  decodeAuthToken,
} from "../../components/utils";
import {
  useChangeOptionStates,
  useCreateESignRequest,
  useSignAndApproveGrant,
  useEsopPlans,
  useBulkStateChangeActionability,
} from "../../queries";
import { useSubmitSignOff } from "../../queries/grantApproval";
import { getGrantApprovalStatus } from "../../api/grantApproval";
import { useAuthStore, useCompanyStore } from "../../store";
import { usePermissionStore } from "../../store/permissionStore";
import { useSignaturePadDialog } from "../../store/useDialogStore";
import { Grant, SignatureAttachment } from "../../types/Grant";
import { ErrorInterface } from "../../types/OptionBuyback";
import { SignOffDecision } from "../../types/GrantApproval";
import { stringFormat } from "../../utils/string";
import { useGetEmailSettings } from "../../queries/siteSettings";
import { evaluateMatrix } from "../../api/allocationMatrix";
import {
  useGetAllMatrices,
  useMultipleEmployeeGrants,
} from "../../queries/allocationMatrix";
import InfoBox from "../../components/shared/InfoBox";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import {
  AllocationMode,
  CostOrGain,
  RuleScope,
} from "../../store/useAllocationMatrixStore";
import { formatDisplayDate } from "../../utils/date";
import StatusLabel from "./StatusLabel";
import { BulkGrantMatrixCell } from "./BulkGrantMatrixCell";
import {
  BulkGrantFactorsCell,
  getMatchedFactors,
} from "./BulkGrantFactorsCell";
import { BulkGrantUtilizationCell } from "./BulkGrantUtilizationCell";
import { BulkGrantNoteCell } from "./BulkGrantNoteCell";
import { BulkGrantOptionsValueCell } from "./BulkGrantOptionsValueCell";
import { useValuationDetails } from "../../queries/Valuation";
import { ValuationSource } from "../../types/ValuationType";
import { BulkRecommendChangeDialog } from "./BulkRecommendChangeDialog";
import { RecommendationActionsMenu } from "../grants/RecommendedChangesAdmin/RecommendationsList";
import {
  calculateAllocationValue,
  getOriginalAllocationRule,
} from "../../utils/allocationMatrixValue";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";

const GRID_HEADER_HEIGHT = 44;
const GRID_ROW_HEIGHT = 48;
const GRID_MAX_VISIBLE_ROWS = 8;

function BulkOptionStateChange({
  grants,
  onClose = () => {},
  onLayoutChange,
}: Readonly<{
  grants: Grant[];
  onClose: () => void;
  onLayoutChange?: (layout: "compact" | "standard" | "wide") => void;
}>) {
  const currency = getCurrencyType();
  const gridRef = useRef<AgGridReact>(null);
  const approvalProgressGridRef = useRef<AgGridReact>(null);
  const [selectedGrants, setSelectedGrants] = useState<Grant[]>(grants);
  const [actionGrants, setActionGrants] = useState<Grant[]>(grants);
  const [allocationReviewComplete, setAllocationReviewComplete] =
    useState(false);
  const [approverTab, setApproverTab] = useState<
    "direct" | "signoff" | "waiting"
  >("direct");
  const [adminApprovalTab, setAdminApprovalTab] = useState<
    "actions" | "progress"
  >("actions");
  const [approvalProgressGroupBy, setApprovalProgressGroupBy] = useState<
    "none" | "grantId" | "approvalStatus"
  >("none");
  const [directDecision, setDirectDecision] = useState("APPROVED");
  const [signOffDecision, setSignOffDecision] = useState<
    "APPROVED" | "REJECTED" | "RECOMMEND_CHANGE"
  >("APPROVED");
  const [adminOverrideWarningOpen, setAdminOverrideWarningOpen] =
    useState(false);
  const pendingOverridePendingSignOffRef = useRef(false);
  const [pendingState, setPendingState] = useState<string | null>(null);
  const [currentLoadingState, setCurrentLoadingState] = useState<string | null>(
    null
  );
  const { mutateAsync: changeOptionStatesAsync, status: isStateChanged } =
    useChangeOptionStates();
  const { mutate: attachSignature, isLoading: signatureBeingAttached } =
    useSignAndApproveGrant();
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const { companyData } = useCompanyStore();
  const { permission } = usePermissionStore();
  const isAdministrator = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.All,
    DefaultAction.All
  );
  const [lapseWithdrawed, setLapseWithdrawed] = useState(false);
  const availableStates = grants[0]?.availableStates ?? [];
  const canOffer = availableStates.includes("OFFERED");
  const isReissue =
    grants.length > 0 &&
    grants.every((grant) => grant.optionHolderState === "OFFER_EXPIRED");
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();
  const { mutate: createEsignRequest, isLoading: eSignSuccess } =
    useCreateESignRequest();
  const actionabilityGrantIds = grants
    .filter((grant) => grant.optionHolderState === "FOR_APPROVAL")
    .map((grant) => grant.optionHolderId)
    .filter(Boolean);
  const { data: actionability = [] } = useBulkStateChangeActionability(
    actionabilityGrantIds
  );

  // Determine which selected grants require the current user's own sign-off
  // (as opposed to a direct admin state change).
  const { accessToken } = useAuthStore();
  const currentUserId = accessToken
    ? decodeAuthToken(accessToken).id?.toString()
    : undefined;
  const { data: plans } = useEsopPlans();
  const { data: matrices } = useGetAllMatrices();
  const planById = useMemo(() => {
    const map: Record<string, any> = {};
    (plans ?? []).forEach((p: any) => {
      map[p.esopPlanId] = p;
    });
    return map;
  }, [plans]);

  const isForApprovalState =
    availableStates.includes("APPROVED") &&
    availableStates.includes("NOT_APPROVED");
  // Approval history remains useful after a grant leaves FOR_APPROVAL. It is
  // intentionally unavailable only for newly created grants, which have not
  // entered an approval workflow yet.
  const approvalProgressGrants = selectedGrants.filter(
    (grant) => grant.optionHolderState !== "CREATED"
  );
  const canLoadApprovalProgress =
    isAdministrator && approvalProgressGrants.length > 0;

  function requiresOwnSignOff(grant: Grant): boolean {
    const config = planById[grant.planid]?.grantApprovalConfig;
    const approvers = config?.approvers ?? [];
    return (
      isForApprovalState &&
      !!config?.requireSignOff &&
      approvers.length > 1 &&
      approvers.some((a: any) => a.userId === currentUserId)
    );
  }

  // Grants requiring the current user's own sign-off are fixed properties of
  // the grant/plan, not of the grid selection — they can never be part of a
  // direct admin state change, in or out of the checkbox selection.
  const signOffGrants = useMemo(
    () => grants.filter(requiresOwnSignOff),
    [grants, planById, currentUserId, isForApprovalState]
  );
  const directSelectableGrants = useMemo(
    () => grants.filter((g) => !requiresOwnSignOff(g)),
    [grants, planById, currentUserId, isForApprovalState]
  );
  // Grants actually selected in the grid that are eligible for a direct
  // admin state change — grants requiring the user's own sign-off are
  // excluded even if their row happens to be checked.
  const directSelectedTargets = isForApprovalState
    ? selectedGrants.filter((g) => !requiresOwnSignOff(g))
    : selectedGrants;

  const actionabilityByGrantId = useMemo(() => {
    const result: Record<string, typeof actionability[number]> = {};
    actionability.forEach((item) => {
      result[item.grantId] = item;
    });
    return result;
  }, [actionability]);
  const directActionTargets = isForApprovalState
    ? selectedGrants.filter(
        (grant) =>
          actionabilityByGrantId[grant.optionHolderId]
            ?.directAdminDecisionAllowed
      )
    : selectedGrants;
  const ownSignOffTargets = selectedGrants.filter(
    (grant) =>
      actionabilityByGrantId[grant.optionHolderId]?.currentUserCanSignOff
  );
  const awaitingOtherSignOffCount = selectedGrants.filter((grant) => {
    const grantActionability = actionabilityByGrantId[grant.optionHolderId];
    return (
      grantActionability?.workflowStatus === "IN_PROGRESS" &&
      !grantActionability.currentUserCanSignOff &&
      !grantActionability.directAdminDecisionAllowed
    );
  }).length;
  const adminPendingSignOffGrants = selectedGrants.filter(
    (grant) =>
      actionabilityByGrantId[grant.optionHolderId]?.workflowStatus ===
      "IN_PROGRESS"
  );
  const allSelectedCanSignOff =
    selectedGrants.length > 0 &&
    selectedGrants.every(
      (grant) =>
        actionabilityByGrantId[grant.optionHolderId]?.currentUserCanSignOff
    );
  const allSelectedCanReceiveAdminDecision =
    selectedGrants.length > 0 &&
    selectedGrants.every(
      (grant) =>
        actionabilityByGrantId[grant.optionHolderId]?.directAdminDecisionAllowed
    );
  const signOffDisabledReason =
    "Select only grants where your sign-off is currently due. Use ‘My action required’ to narrow the list.";
  const adminDecisionDisabledReason =
    "All selected grants must have completed sign-offs before a final admin decision can be made.";
  const hasMixedApprovalSelection =
    isForApprovalState &&
    selectedGrants.length > 0 &&
    !allSelectedCanSignOff &&
    !allSelectedCanReceiveAdminDecision;

  const signOffStatusQueries = useQueries(
    signOffGrants.map((grant) => ({
      queryKey: ["grantApprovalStatus", grant.optionHolderId],
      queryFn: () => getGrantApprovalStatus(grant.optionHolderId),
      enabled: !!grant.optionHolderId,
      staleTime: 1000 * 60 * 2,
    }))
  );

  const approvalProgressQueries = useQueries(
    approvalProgressGrants.map((grant) => ({
      queryKey: ["grantApprovalProgress", grant.optionHolderId],
      queryFn: () => getGrantApprovalStatus(grant.optionHolderId),
      enabled: canLoadApprovalProgress && !!grant.optionHolderId,
      staleTime: 1000 * 30,
    }))
  );
  const approvalProgressLoaded =
    approvalProgressQueries.length > 0 &&
    approvalProgressQueries.every((query) => !query.isLoading);
  const hasApprovalProgressRows = approvalProgressQueries.some(
    (query) => (query.data?.length ?? 0) > 0
  );
  const canViewApprovalProgress =
    canLoadApprovalProgress &&
    approvalProgressLoaded &&
    hasApprovalProgressRows;

  const { mutateAsync: submitSignOffAsync } = useSubmitSignOff();
  const [signOffActionLoading, setSignOffActionLoading] =
    useState<SignOffDecision | null>(null);

  async function handleBulkSignOff(decision: SignOffDecision) {
    setSignOffActionLoading(decision);
    const targets = signOffGrants
      .map((_grant, idx) => {
        const rows = signOffStatusQueries[idx]?.data ?? [];
        const myRow = rows.find(
          (r) => r.userId === currentUserId && r.status === "PENDING"
        );
        return myRow ? { signOffId: myRow.id } : null;
      })
      .filter((t): t is { signOffId: string } => !!t);

    if (targets.length === 0) {
      setSignOffActionLoading(null);
      return;
    }

    try {
      await Promise.all(
        targets.map((t) =>
          submitSignOffAsync({
            signOffId: t.signOffId,
            payload: { decision },
          })
        )
      );
      toast(
        `Sign-off ${
          decision === SignOffDecision.APPROVED ? "approved" : "rejected"
        } for ${targets.length} grant(s).`,
        { type: "success", autoClose: 2000 }
      );
      onClose();
    } catch (err: any) {
      toast(err?.message || "Failed to submit sign-off", {
        type: "error",
        autoClose: 2000,
      });
    } finally {
      setSignOffActionLoading(null);
    }
  }

  async function handleActionableBulkSignOff(
    decision: SignOffDecision,
    targets = ownSignOffTargets,
    closeAfterSubmit = true
  ) {
    if (!targets.length) return;
    setSignOffActionLoading(decision);
    try {
      await changeOptionStatesAsync({
        optionHolderIds: targets.map((grant) => grant.optionHolderId),
        optionHolderState: "FOR_APPROVAL" as any,
        toSubState:
          decision === SignOffDecision.APPROVED ? "approved" : "rejected",
        note: note.trim(),
        mailToBeSent: false,
        lapseWithdrawed: false,
      });
      toast(`Recorded sign-off for ${targets.length} grant(s).`, {
        type: "success",
        autoClose: 2000,
      });
      if (closeAfterSubmit) {
        onClose();
      } else {
        const targetIds = new Set(targets.map((grant) => grant.optionHolderId));
        setActionGrants((current) =>
          current.filter((grant) => !targetIds.has(grant.optionHolderId))
        );
      }
    } catch (err: any) {
      toast(err?.message || "Failed to submit sign-off", {
        type: "error",
        autoClose: 2000,
      });
    } finally {
      setSignOffActionLoading(null);
    }
  }

  // Determine current share price for allocation matrix comparisons
  const { data: _valuationData, isPlaceholderData } = useValuationDetails();

  const currentSharePrice = useMemo(() => {
    const isPublic = companyData?.companyMetaData.typeOfTheCompany === "PUBLIC";
    if (isPublic) {
      const publicPps = companyData?.companyMetaData?.pps;
      if (publicPps) return publicPps;
    } else if (
      _valuationData &&
      !isPlaceholderData &&
      _valuationData.length > 0
    ) {
      let valuation = _valuationData.find(
        (v: any) => v.valuationSource === ValuationSource.COMPANY
      );
      if (!valuation) {
        valuation = _valuationData[0];
      }
      if (valuation?.pps) return valuation.pps;
    }
    return 0;
  }, [_valuationData, isPlaceholderData, companyData]);

  // Batch load all employee grants for allocation calculation
  const uniqueEmployeeIds = useMemo(
    () => [...new Set(grants.map((g) => g.employeeId))],
    [grants]
  );

  const employeeGrantsQueries = useMultipleEmployeeGrants(uniqueEmployeeIds);

  const allEmployeeGrantsData = useMemo(() => {
    const map: Record<string, Grant[]> = {};
    uniqueEmployeeIds.forEach((empId, idx) => {
      map[empId] = employeeGrantsQueries[idx]?.data || [];
    });
    return map;
  }, [uniqueEmployeeIds, employeeGrantsQueries]);

  // Pre-calculate allocated values for all grants
  const allocatedByGrantId = useMemo(() => {
    const map: Record<string, number> = {};
    grants.forEach((grant) => {
      const employeeGrants = (
        allEmployeeGrantsData[grant.employeeId] || []
      ).filter(
        (g: Grant) =>
          g.optionHolderState !== "WITHDRAW" &&
          g.grantIdentifier !== grant.grantIdentifier
      );

      const rule = getOriginalAllocationRule(
        matrices,
        grant.allocationMatrixId,
        grant.matchedRule as any
      );
      const costOrGain = rule?.costOrGain as CostOrGain;

      map[grant.grantIdentifier] = employeeGrants.reduce(
        (sum: number, g: Grant) =>
          sum +
          calculateAllocationValue({
            options: g.optionsGranted || 0,
            ratio: g.ratio,
            fmv: currentSharePrice,
            strikePrice: g.grantPrice || 0,
            allocationMode: rule?.allocationMode,
            costOrGain,
          }),
        0
      );
    });
    return map;
  }, [grants, allEmployeeGrantsData, currentSharePrice, matrices]);

  // Calculate comparable value for allocation matrix limit checking
  const getGrantComparableValue = (grant: Grant): number => {
    const rule = getOriginalAllocationRule(
      matrices,
      grant.allocationMatrixId,
      grant.matchedRule as any
    );
    if (rule?.allocationMode === AllocationMode.ABSOLUTE_VALUE) {
      return calculateAllocationValue({
        options: grant.optionsGranted || 0,
        ratio: grant.ratio,
        fmv: currentSharePrice,
        strikePrice: grant.grantPrice || 0,
        allocationMode: rule.allocationMode,
        costOrGain: rule.costOrGain,
      });
    }
    return grant.optionsGranted || 0;
  };

  const isGrantAboveLimit = (grant: Grant): boolean => {
    const rule = getOriginalAllocationRule(
      matrices,
      grant.allocationMatrixId,
      grant.matchedRule as any
    );
    if (rule?.quantumMax == null) return false;

    const currentGrantValue = getGrantComparableValue(grant);
    const isPerEmployee = rule?.scope === RuleScope.PER_EMPLOYEE;
    const totalValue = isPerEmployee
      ? (allocatedByGrantId[grant.grantIdentifier] || 0) + currentGrantValue
      : currentGrantValue;

    return totalValue > rule.quantumMax;
  };

  const isGrantBelowLimit = (grant: Grant): boolean => {
    const rule = getOriginalAllocationRule(
      matrices,
      grant.allocationMatrixId,
      grant.matchedRule as any
    );
    if (rule?.quantumMin == null) return false;

    const currentGrantValue = getGrantComparableValue(grant);
    const isPerEmployee = rule?.scope === RuleScope.PER_EMPLOYEE;
    const totalValue = isPerEmployee
      ? (allocatedByGrantId[grant.grantIdentifier] || 0) + currentGrantValue
      : currentGrantValue;

    return totalValue < rule.quantumMin;
  };

  const isGrantDeviating = (grant: Grant): boolean =>
    isGrantAboveLimit(grant) || isGrantBelowLimit(grant);

  useEffect(() => {
    if (showSignaturePad.signatureType === "regular") {
      handleSignaturePad(showSignaturePad.signature ?? "");
    }
    if (showSignaturePad.signatureType === "esign") {
      createDigiESignRequest();
    }
  }, [showSignaturePad]);

  const { data: emailSettings, isFetched } = useGetEmailSettings();

  const useAllocationMatrixForGranting =
    companyData?.companySettings?.grantLetterSettings
      ?.useAllocationMatrixForGranting;
  const showValueAtGrantToAdmin =
    companyData?.companySettings?.grantLetterSettings?.showValueAtGrantToAdmin;

  const hasAnyAllocationMatrix =
    useAllocationMatrixForGranting && grants.some((g) => g.allocationMatrixId);
  const matchedFactorQueries = useQueries(
    grants.map((grant) => ({
      queryKey: [
        "allocationMatrixEvaluation",
        grant.allocationMatrixId,
        grant.employeeId,
      ],
      queryFn: async () => {
        const response = await evaluateMatrix(
          grant.allocationMatrixId!,
          grant.employeeId
        );
        return response.data?.data ?? null;
      },
      enabled: Boolean(
        hasAnyAllocationMatrix && grant.allocationMatrixId && grant.employeeId
      ),
    }))
  );
  const matchedFactorsByGrantId = useMemo(() => {
    const factors: Record<string, string[]> = {};
    grants.forEach((grant, index) => {
      factors[grant.optionHolderId] = getMatchedFactors(
        matchedFactorQueries[index]?.data
      );
    });
    return factors;
  }, [grants, matchedFactorQueries]);
  const gridRows = useMemo(
    () =>
      grants.map((grant) => ({
        ...grant,
        matchedFactors: matchedFactorsByGrantId[grant.optionHolderId] ?? [],
      })),
    [grants, matchedFactorsByGrantId]
  );
  const uniquePlanCount = new Set(grants.map((g) => g.planid)).size;
  const deviatingCount = grants.filter(isGrantDeviating).length;
  const allocationChecksLoading =
    hasAnyAllocationMatrix &&
    employeeGrantsQueries.some((query) => query.isLoading);
  const showAllocationReview =
    isForApprovalState &&
    !isAdministrator &&
    hasAnyAllocationMatrix &&
    deviatingCount > 0 &&
    !allocationReviewComplete;

  useEffect(() => {
    setActionGrants(grants);
    setAllocationReviewComplete(false);
    setApproverTab("direct");
  }, [grants]);

  useEffect(() => {
    onLayoutChange?.(
      showAllocationReview
        ? "compact"
        : adminOverrideWarningOpen
        ? "compact"
        : isForApprovalState && !isAdministrator
        ? "standard"
        : "wide"
    );
  }, [
    adminOverrideWarningOpen,
    isAdministrator,
    isForApprovalState,
    onLayoutChange,
    showAllocationReview,
  ]);

  function createDigiESignRequest() {
    const eSignRequestObject = {
      entityIds: grants.map((grant) => grant.optionHolderId),
      type: "grant",
    };
    setShowSignaturePad({ open: false, isLoading: !eSignSuccess });
    createEsignRequest(eSignRequestObject, {
      onSuccess: () => {
        setShowSignaturePad({ open: false, isLoading: eSignSuccess });
      },
      onError: (err: any) => {
        const error = err.response.data as ErrorInterface;
        toast(`${error.errors}`, { autoClose: 2000, type: "error" });
        setShowSignaturePad({ open: false, isLoading: eSignSuccess });
      },
    });
  }

  const stateChangeTargetsRef = useRef<Grant[]>(selectedGrants);

  async function executeStateChange(
    state: string,
    targetGrants: Grant[] = selectedGrants,
    closeAfterSubmit = true,
    overridePendingSignOff = false
  ) {
    if (targetGrants.length === 0) {
      setError("Please select at least one grant");
      return;
    }
    if (!targetGrants[0].optionHolderId) return;
    if (note === "" && state === "WITHDRAW") {
      setError("Note required");
      return;
    }
    const acceptanceDays = Number(acceptanceNumber);
    if (
      state === "OFFERED" &&
      overrideAcceptanceWindow &&
      (!Number.isInteger(acceptanceDays) || acceptanceDays < 0)
    ) {
      setError("Acceptance window must be a whole number of days or zero");
      return;
    }
    stateChangeTargetsRef.current = targetGrants;
    pendingOverridePendingSignOffRef.current = overridePendingSignOff;
    if (
      companyData?.companySettings.grantLetterSettings
        ?.requestApproverSignature &&
      state === "APPROVED"
    ) {
      setPendingState(state);
      toast("Please Sign In order to approve the grant", {
        type: "info",
        autoClose: 3000,
      });
      setShowSignaturePad({ open: true, entityType: "grant" });
      return;
    }
    setCurrentLoadingState(state);
    try {
      await changeOptionStatesAsync({
        optionHolderIds: targetGrants.map((grant) => grant.optionHolderId),
        optionHolderState: state,
        note: note.trim(),
        mailToBeSent: state === "OFFERED" ? emailCheck : false,
        ...(state === "OFFERED" && overrideAcceptanceWindow
          ? { acceptanceNumber: acceptanceDays }
          : {}),
        lapseWithdrawed,
        overridePendingSignOff,
      });
      toast("State Change Initiated Successfully! Please monitor the status.", {
        type: "success",
      });
      if (closeAfterSubmit) {
        onClose();
      } else {
        const targetIds = new Set(
          targetGrants.map((grant) => grant.optionHolderId)
        );
        setActionGrants((current) =>
          current.filter((grant) => !targetIds.has(grant.optionHolderId))
        );
      }
    } catch (err: any) {
      const errMsg =
        err.response?.data?.reason ||
        err.response?.data?.errorRaw ||
        "Failed to change state";
      setError(errMsg);
      toast(errMsg, { type: "error", autoClose: 2000 });
    }
  }

  function handleSignaturePad(signature: string) {
    if (signature.includes("data:image/png;base64,")) {
      signature = signature.replace("data:image/png;base64,", "");
    }
    const ids = stateChangeTargetsRef.current.map(
      (grant) => grant.optionHolderId
    );
    const signatureAttachmentObj: SignatureAttachment = {
      signature,
      entityIds: ids,
      origin: "grant",
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(pendingOverridePendingSignOffRef.current
        ? { overridePendingSignOff: true }
        : {}),
    };
    setShowSignaturePad({ open: false, isLoading: !signatureBeingAttached });
    attachSignature(signatureAttachmentObj, {
      onSuccess: () => {
        toast("State Changed Successfully!", {
          type: "success",
          autoClose: 2000,
        });
        onClose();
        setShowSignaturePad({ open: false, isLoading: signatureBeingAttached });
      },
      onError: (err: any) => {
        const error = err.response.data as ErrorInterface;
        onClose();
        toast(`${error.errors}`, { autoClose: 2000, type: "error" });
      },
    });
  }

  const matrixNameById = (matrices ?? []).reduce<Record<string, string>>(
    (acc, m: any) => {
      if (m.id) acc[m.id] = m.name;
      return acc;
    },
    {}
  );

  const sideBar: SideBarDef = {
    toolPanels: [
      {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
        minWidth: 180,
        maxWidth: 400,
        width: 250,
      },
    ],
  };

  const isGridAutoHeight = grants.length <= GRID_MAX_VISIBLE_ROWS;

  const [emailCheck, setEmailCheck] = useState(false);
  const [overrideAcceptanceWindow, setOverrideAcceptanceWindow] =
    useState(false);
  const [acceptanceNumber, setAcceptanceNumber] = useState("");
  const siteOfferedEmailsEnabled =
    emailSettings?.sendEmailsToEmployees &&
    emailSettings?.sendGrantEmailsToEmployees &&
    emailSettings?.sendGrantOfferedEmailsToEmployees;
  const generateCredentialsOnGrantOffer =
    emailSettings?.generateCredentialsOnGrantOffer ?? false;

  useEffect(() => {
    if (canOffer) {
      setEmailCheck(siteOfferedEmailsEnabled || false);
      setAcceptanceNumber(
        String(
          companyData?.companySettings?.grantLetterSettings?.acceptanceNumber ??
            0
        )
      );
    }
  }, [
    isFetched,
    canOffer,
    siteOfferedEmailsEnabled,
    companyData?.companySettings?.grantLetterSettings?.acceptanceNumber,
  ]);

  function getColumnDefs(): ColDef[] {
    const cols: ColDef[] = [
      ...(isForApprovalState
        ? []
        : [
            {
              headerName: "",
              checkboxSelection: true,
              headerCheckboxSelection: true,
              width: 44,
              minWidth: 44,
              maxWidth: 44,
              pinned: "left" as const,
              resizable: false,
              sortable: false,
              filter: false,
              suppressMenu: true,
              cellClass: "ag-center-aligned",
            },
          ]),
      {
        headerName: "Holder",
        field: "optionHolderName",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Grant Info",
        field: "grantIdentifier",
        flex: 1.2,
        minWidth: 140,
      },
      ...(isForApprovalState && signOffGrants.length > 0
        ? [
            {
              headerName: "Approval",
              flex: 1,
              minWidth: 170,
              cellRenderer: (params: any) =>
                requiresOwnSignOff(params.data) ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-sm">
                    <Icon icon="ri:shield-check-line" width={12} />
                    Needs your sign-off
                  </span>
                ) : (
                  <span className="text-xs text-gray-light">—</span>
                ),
            },
          ]
        : []),
      {
        headerName: showValueAtGrantToAdmin
          ? "Value at Grant"
          : "Options Granted",
        flex: 1,
        minWidth: 140,
        cellClass: "cell-right",
        headerClass: "cell-right",
        cellRenderer: (params: any) => (
          <BulkGrantOptionsValueCell
            grant={params.data}
            showValueAtGrantToAdmin={showValueAtGrantToAdmin ?? false}
          />
        ),
      },
      ...(hasAnyAllocationMatrix
        ? [
            {
              headerName: "Matched Factors",
              field: "matchedFactors",
              flex: 1.3,
              minWidth: 200,
              autoHeight: true,
              filter: "agSetColumnFilter",
              cellRenderer: (params: any) => (
                <BulkGrantFactorsCell
                  matchedFactors={
                    params.data.matchedFactors ??
                    matchedFactorsByGrantId[params.data.optionHolderId] ??
                    []
                  }
                  api={params.api}
                />
              ),
            },
            {
              headerName: "Allocated",
              flex: 1,
              minWidth: 130,
              autoHeight: true,
              cellRenderer: (params: any) => (
                <BulkGrantUtilizationCell
                  grant={params.data}
                  allocatedValue={
                    allocatedByGrantId[params.data.grantIdentifier]
                  }
                  priorGrantCount={
                    (
                      allEmployeeGrantsData[params.data.employeeId] || []
                    ).filter(
                      (g: Grant) =>
                        g.optionHolderState !== "WITHDRAW" &&
                        g.grantIdentifier !== params.data.grantIdentifier
                    ).length
                  }
                  currentSharePrice={currentSharePrice}
                />
              ),
            },
            {
              headerName: "Matrix",
              flex: 1.5,
              minWidth: 200,
              autoHeight: true,
              cellRenderer: (params: any) => (
                <BulkGrantMatrixCell
                  grant={params.data}
                  matrixName={
                    matrixNameById[params.data.allocationMatrixId || ""] ||
                    params.data.allocationMatrixName
                  }
                />
              ),
            },
            {
              headerName: "Limit Check",
              flex: 1,
              minWidth: 160,
              cellRenderer: (params: any) => {
                const grant = params.data as Grant;
                if (isGrantAboveLimit(grant) || isGrantBelowLimit(grant)) {
                  return (
                    <HStack className="items-center gap-1">
                      <Icon
                        icon="ri:alert-line"
                        height={13}
                        className="text-primary shrink-0"
                      />
                      <span className="text-xs text-primary font-medium">
                        {isGrantAboveLimit(grant)
                          ? "Above Permissible Limit"
                          : "Below Permissible Limit"}
                      </span>
                    </HStack>
                  );
                }
                return <span className="text-xs text-gray-light">—</span>;
              },
              filterValueGetter: (params: any) => {
                const grant = params.data as Grant;
                if (isGrantAboveLimit(grant)) {
                  return "Above Permissible Limit";
                }
                if (isGrantBelowLimit(grant)) {
                  return "Below Permissible Limit";
                }
                return "";
              },
            },
          ]
        : []),
      {
        headerName: "Notes",
        field: "note",
        flex: 1.6,
        filter: false,
        sortable: false,
        suppressMenu: true,
        minWidth: 260,
        cellRenderer: (params: any) => (
          <BulkGrantNoteCell note={params.value} />
        ),
      },
    ];
    return cols;
  }

  // FOR_APPROVAL has a distinct approver workflow. Keep the selected grants
  // separated by what this user can actually do instead of leaving a mixed
  // selection with ambiguous, disabled actions.
  const actionabilityReady =
    actionGrants.length === 0 || actionability.length >= actionGrants.length;
  const directApprovalGrants = actionGrants.filter((grant) => {
    const item = actionabilityByGrantId[grant.optionHolderId];
    return (
      item?.workflowStatus === "NO_WORKFLOW" && item.directAdminDecisionAllowed
    );
  });
  const signOffDueGrants = actionGrants.filter(
    (grant) =>
      actionabilityByGrantId[grant.optionHolderId]?.currentUserCanSignOff
  );
  const waitingForAnotherApproverGrants = actionGrants.filter(
    (grant) =>
      !directApprovalGrants.some(
        (item) => item.optionHolderId === grant.optionHolderId
      ) &&
      !signOffDueGrants.some(
        (item) => item.optionHolderId === grant.optionHolderId
      )
  );
  const activeApproverGrants =
    approverTab === "direct"
      ? directApprovalGrants
      : approverTab === "signoff"
      ? signOffDueGrants
      : waitingForAnotherApproverGrants;
  const approverColumns = getColumnDefs().filter(
    (column) => column.headerName !== "" && column.headerName !== "Approval"
  );
  const approverGridHeight =
    Math.min(5, Math.max(3, activeApproverGrants.length)) * GRID_ROW_HEIGHT +
    GRID_HEADER_HEIGHT +
    2;

  if (isForApprovalState && !isAdministrator) {
    if (allocationChecksLoading || !actionabilityReady) {
      return (
        <div className="w-full">
          <div className="px-10 py-7 text-lg font-medium border-b flex items-center justify-between">
            <h6>Bulk approval actions</h6>
            <button
              className="text-gray-light hover:text-primaryText"
              onClick={onClose}
            >
              <Icon icon="ri:close-line" height={22} />
            </button>
          </div>
          <div className="px-10 py-12">
            <InfoBox message="Checking the selected grants and their approval requirements…" />
          </div>
        </div>
      );
    }

    if (showAllocationReview) {
      const aboveLimitCount = grants.filter(isGrantAboveLimit).length;
      const belowLimitCount = grants.filter(isGrantBelowLimit).length;
      const flaggedGrants = grants.filter(isGrantDeviating);
      const formatLimitValue = (grant: Grant, value: number) =>
        (grant.matchedRule as any)?.allocationMode ===
        AllocationMode.ABSOLUTE_VALUE
          ? formatCurrency(value, currency)
          : value.toLocaleString();
      return (
        <div className="w-full">
          <div className="px-7 py-5 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
                <Icon icon="ri:alert-line" height={20} />
              </span>
              <div>
                <h6 className="text-lg font-semibold text-primaryText">
                  Allocation limit warning
                </h6>
                <p className="mt-0.5 text-sm text-secondaryText">
                  Review the flagged grants before continuing.
                </p>
              </div>
            </div>
            <button
              className="text-gray-light hover:text-primaryText"
              onClick={onClose}
            >
              <Icon icon="ri:close-line" height={22} />
            </button>
          </div>
          <div className="px-7 py-6 space-y-5">
            <InfoBox
              variant="warning"
              message={`${deviatingCount} selected grant${
                deviatingCount === 1 ? " is" : "s are"
              } outside its allocation-matrix permissible limit.${
                aboveLimitCount ? ` ${aboveLimitCount} above limit.` : ""
              }${belowLimitCount ? ` ${belowLimitCount} below limit.` : ""}`}
            />
            <div className="overflow-hidden rounded-md border border-borderColor">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Grant</th>
                    <th className="px-4 py-3 font-semibold">Employee</th>
                    <th className="px-4 py-3 font-semibold">Matrix</th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Options granted
                    </th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Value at grant
                    </th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Allocated
                    </th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Permissible range
                    </th>
                    <th className="px-4 py-3 font-semibold">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {flaggedGrants.map((grant) => {
                    const rule = grant.matchedRule as any;
                    const allocated =
                      (allocatedByGrantId[grant.grantIdentifier] || 0) +
                      getGrantComparableValue(grant);
                    const result = isGrantAboveLimit(grant)
                      ? "Above permissible limit"
                      : "Below permissible limit";
                    return (
                      <tr key={grant.optionHolderId}>
                        <td className="px-4 py-3 font-mono font-medium text-gray-800">
                          {grant.grantIdentifier}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {grant.optionHolderName}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {matrixNameById[grant.allocationMatrixId || ""] ||
                            grant.allocationMatrixName ||
                            "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                          {(grant.optionsGranted || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-800">
                          {formatCurrency(grant.sharePrice || 0, currency)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                          {formatLimitValue(grant, allocated)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {rule?.quantumMin != null
                            ? formatLimitValue(grant, rule.quantumMin)
                            : "—"}
                          {" – "}
                          {rule?.quantumMax != null
                            ? formatLimitValue(grant, rule.quantumMax)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 font-medium text-yellow-700">
                          {result}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-secondaryText">
              Excluded grants remain unchanged and can be reviewed separately.
            </p>
          </div>
          <HStack className="justify-end gap-3 px-7 py-5 border-t">
            <ButtonPrimary1
              onClick={() => {
                setActionGrants(
                  grants.filter((grant) => !isGrantDeviating(grant))
                );
                setAllocationReviewComplete(true);
              }}
            >
              Exclude flagged grants ({deviatingCount})
            </ButtonPrimary1>
            <ButtonPrimary
              onClick={() => {
                setActionGrants(grants);
                setAllocationReviewComplete(true);
              }}
            >
              Include all grants
            </ButtonPrimary>
          </HStack>
        </div>
      );
    }

    return (
      <div className="w-full">
        <div className="px-10 py-7 text-lg font-medium border-b flex items-center justify-between">
          <div>
            <h6>Bulk approval workflow</h6>
            <p className="mt-1 text-sm font-normal text-secondaryText">
              Review the selected grants by the approval action available to
              you.
            </p>
          </div>
          <button
            className="text-gray-light hover:text-primaryText"
            onClick={onClose}
          >
            <Icon icon="ri:close-line" height={22} />
          </button>
        </div>

        <VStack className="px-10 py-7 gap-5">
          <div className="inline-flex self-start rounded-md border border-borderColor bg-white">
            <MuiTooltip
              title="No sign-off is required for these grants because the plan does not have a sign-off process configured."
              placement="bottom"
              arrow
            >
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  approverTab === "direct"
                    ? "bg-slate-100 text-primaryText"
                    : "text-secondaryText hover:bg-gray-50"
                }`}
                onClick={() => setApproverTab("direct")}
              >
                No sign-off is required ({directApprovalGrants.length})
              </button>
            </MuiTooltip>
            <MuiTooltip
              title="Your sign-off is required for these grants. Please review them and submit your sign-off."
              placement="bottom"
              arrow
            >
              <button
                type="button"
                className={`border-l border-borderColor px-4 py-2 text-sm font-medium ${
                  approverTab === "signoff"
                    ? "bg-slate-100 text-primaryText"
                    : "text-secondaryText hover:bg-gray-50"
                }`}
                onClick={() => setApproverTab("signoff")}
              >
                Your sign-off is required ({signOffDueGrants.length})
              </button>
            </MuiTooltip>
            <MuiTooltip
              title="These grants are awaiting sign-off from another approver. You are included in the approval sequence, but it is not yet your turn to sign off."
              placement="bottom"
              arrow
            >
              <button
                type="button"
                className={`border-l border-borderColor px-4 py-2 text-sm font-medium ${
                  approverTab === "waiting"
                    ? "bg-slate-100 text-primaryText"
                    : "text-secondaryText hover:bg-gray-50"
                }`}
                onClick={() => setApproverTab("waiting")}
              >
                Pending another approver&apos;s sign-off (
                {waitingForAnotherApproverGrants.length})
              </button>
            </MuiTooltip>
          </div>

          <div
            className="bulk-state-grid ag-theme-material border border-borderColor rounded-lg overflow-hidden"
            style={{ height: approverGridHeight, width: "100%" }}
          >
            <AgGridReact
              rowData={activeApproverGrants}
              columnDefs={approverColumns}
              headerHeight={GRID_HEADER_HEIGHT}
              rowHeight={GRID_ROW_HEIGHT}
              suppressRowClickSelection
              suppressCellFocus
              overlayNoRowsTemplate={`<div style="padding: 24px; color: #6b7280; font-size: 13px;">No grants are available in this category.</div>`}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: false,
                filter: false,
              }}
            />
          </div>

          {approverTab === "direct" && directApprovalGrants.length > 0 && (
            <div className="max-w-sm">
              <Label>Action</Label>
              <Select
                className="mt-2 w-full"
                value={directDecision}
                onChange={(event) => setDirectDecision(event.target.value)}
                options={["APPROVED", "NOT_APPROVED"]}
                textGetter={(state) =>
                  state === "APPROVED" ? "Approve" : "Not approve"
                }
              />
              <div className="mt-4">
                <Label>
                  Note{" "}
                  <span className="text-xs text-secondaryText">(optional)</span>
                </Label>
                <Input
                  className="mt-2"
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
            </div>
          )}

          {approverTab === "signoff" && signOffDueGrants.length > 0 && (
            <>
              <div className="max-w-sm">
                <Label>Action</Label>
                <Select
                  className="mt-2 w-full"
                  value={signOffDecision}
                  onChange={(event) =>
                    setSignOffDecision(
                      event.target.value as typeof signOffDecision
                    )
                  }
                  options={["APPROVED", "REJECTED", "RECOMMEND_CHANGE"]}
                  textGetter={(state) =>
                    state === "APPROVED"
                      ? "Approve sign-off"
                      : state === "REJECTED"
                      ? "Reject sign-off"
                      : "Recommend change"
                  }
                />
              </div>
              {signOffDecision === "RECOMMEND_CHANGE" ? (
                <BulkRecommendChangeDialog
                  embedded
                  open
                  grants={signOffDueGrants}
                  onClose={onClose}
                  onSubmitted={() => {
                    const targetIds = new Set(
                      signOffDueGrants.map((grant) => grant.optionHolderId)
                    );
                    setActionGrants((current) =>
                      current.filter(
                        (grant) => !targetIds.has(grant.optionHolderId)
                      )
                    );
                  }}
                />
              ) : (
                <div className="w-1/2">
                  <Label>
                    Note{" "}
                    <span className="text-xs text-secondaryText">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    className="mt-2"
                    type="text"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </div>
              )}
            </>
          )}

          {approverTab === "waiting" && (
            <InfoBox message="No approver action is available for these grants yet. They are either waiting for another approver or are ready for an administrator’s final decision." />
          )}
        </VStack>

        {approverTab !== "waiting" &&
          activeApproverGrants.length > 0 &&
          !(
            approverTab === "signoff" && signOffDecision === "RECOMMEND_CHANGE"
          ) && (
            <HStack className="justify-between gap-3 px-10 py-6 border-t">
              <ButtonPrimary1
                loading={
                  approverTab === "direct"
                    ? currentLoadingState === directDecision &&
                      isStateChanged === "loading"
                    : signOffActionLoading === signOffDecision
                }
                onClick={() =>
                  approverTab === "direct"
                    ? executeStateChange(
                        directDecision,
                        directApprovalGrants,
                        false
                      )
                    : handleActionableBulkSignOff(
                        signOffDecision as SignOffDecision,
                        signOffDueGrants,
                        false
                      )
                }
              >
                Submit and stay
              </ButtonPrimary1>
              <ButtonPrimary
                loading={
                  approverTab === "direct"
                    ? currentLoadingState === directDecision &&
                      isStateChanged === "loading"
                    : signOffActionLoading === signOffDecision
                }
                onClick={() =>
                  approverTab === "direct"
                    ? executeStateChange(directDecision, directApprovalGrants)
                    : handleActionableBulkSignOff(
                        signOffDecision as SignOffDecision,
                        signOffDueGrants
                      )
                }
              >
                Submit and close
              </ButtonPrimary>
            </HStack>
          )}
      </div>
    );
  }

  if (isForApprovalState && isAdministrator && adminOverrideWarningOpen) {
    return (
      <div className="w-full">
        <div className="px-7 py-5 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
              <Icon icon="ri:alert-line" height={20} />
            </span>
            <div>
              <h6 className="text-lg font-semibold text-primaryText">
                Approve despite pending sign-offs?
              </h6>
              <p className="mt-0.5 text-sm text-secondaryText">
                This is an administrator override.
              </p>
            </div>
          </div>
          <button
            className="text-gray-light hover:text-primaryText"
            onClick={() => setAdminOverrideWarningOpen(false)}
          >
            <Icon icon="ri:close-line" height={22} />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          <InfoBox
            variant="warning"
            message={`${adminPendingSignOffGrants.length} selected grant${
              adminPendingSignOffGrants.length === 1
                ? " still has"
                : "s still have"
            } outstanding sign-offs. Approving now moves every selected grant to Approved and bypasses those pending approvals.`}
          />
          <div className="overflow-hidden rounded-md border border-borderColor">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Grant</th>
                  <th className="px-4 py-3 font-semibold">Employee</th>
                  <th className="px-4 py-3 font-semibold">Pending sign-offs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {adminPendingSignOffGrants.map((grant) => (
                  <tr key={grant.optionHolderId}>
                    <td className="px-4 py-3 font-mono font-medium text-gray-800">
                      {grant.grantIdentifier}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {grant.optionHolderName}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {actionabilityByGrantId[grant.optionHolderId]
                        ?.pendingSignOffCount ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <HStack className="justify-end gap-3 px-7 py-5 border-t">
          <ButtonPrimary1 onClick={() => setAdminOverrideWarningOpen(false)}>
            Go back
          </ButtonPrimary1>
          <ButtonPrimary
            loading={
              currentLoadingState === "APPROVED" && isStateChanged === "loading"
            }
            onClick={() =>
              executeStateChange("APPROVED", selectedGrants, true, true)
            }
          >
            Approve grants anyway
          </ButtonPrimary>
        </HStack>
      </div>
    );
  }

  const adminApprovalTabs = (
    <div className="inline-flex overflow-hidden rounded-md border border-borderColor bg-white">
      <button
        type="button"
        className={[
          "px-4 py-2 text-sm font-medium",
          adminApprovalTab === "actions"
            ? "bg-slate-100 text-primaryText"
            : "text-secondaryText hover:bg-gray-50",
        ].join(" ")}
        onClick={() => setAdminApprovalTab("actions")}
      >
        {isForApprovalState
          ? `Approval actions (${selectedGrants.length})`
          : "Bulk update"}
      </button>
      <button
        type="button"
        className={[
          "border-l border-borderColor px-4 py-2 text-sm font-medium",
          adminApprovalTab === "progress"
            ? "bg-slate-100 text-primaryText"
            : "text-secondaryText hover:bg-gray-50",
        ].join(" ")}
        onClick={() => setAdminApprovalTab("progress")}
      >
        Approval progress ({approvalProgressGrants.length})
      </button>
    </div>
  );

  if (canViewApprovalProgress && adminApprovalTab === "progress") {
    const progressLoading = approvalProgressQueries.some(
      (query) => query.isLoading
    );
    const progressRows = approvalProgressGrants.flatMap((grant, index) =>
      (approvalProgressQueries[index]?.data || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((row, approvalIndex) => {
          const proposedChanges =
            row.recommendations?.[0]?.proposedChanges ?? undefined;
          const snapshot = proposedChanges?.conversionSnapshot;
          const allocationRule = getOriginalAllocationRule(
            matrices,
            grant.allocationMatrixId,
            grant.matchedRule as any
          );
          const originalOptions = grant.optionsGranted || 0;
          const originalValue = calculateAllocationValue({
            options: originalOptions,
            ratio: snapshot?.conversionRatio ?? grant.ratio,
            fmv: snapshot?.fmv ?? currentSharePrice,
            strikePrice: snapshot?.grantPrice ?? grant.grantPrice ?? 0,
            // The comparison needs the monetary value in every rule mode,
            // while the adjacent number remains the actual granted options.
            allocationMode: AllocationMode.ABSOLUTE_VALUE,
            costOrGain:
              snapshot?.costOrGain ??
              allocationRule?.costOrGain ??
              CostOrGain.COST,
          });

          return {
            id: row.id,
            queryIndex: index,
            grantId: grant.optionHolderId,
            grantIdentifier: grant.grantIdentifier,
            employeeName: grant.optionHolderName,
            // An approver may be skipped for this grant (for example, when
            // they are not the employee's manager). Number only the rows
            // that exist for this grant, rather than exposing the configured
            // workflow order and its possible gaps.
            approvalStep: approvalIndex + 1,
            approverName: row.approverName,
            approverEmail: row.approverEmail,
            status: row.status,
            note: row.comment,
            respondedAt: row.metaData?.respondedAt,
            originalOptions,
            originalValue,
            proposedChanges,
          };
        })
    );
    const formatRecommendation = (changes?: Record<string, any>) => {
      if (!changes) return "—";
      const displayValue = (value: any) =>
        Number.isFinite(Number(value))
          ? Number(value).toLocaleString()
          : String(value);
      const values = [
        changes.optionsGranted !== undefined &&
          `Options: ${displayValue(changes.optionsGranted)}`,
        changes.valueOfOptions !== undefined &&
          `Value: ${formatCurrency(Number(changes.valueOfOptions), currency)}`,
      ].filter(Boolean);
      return values.length ? values.join(" · ") : "—";
    };
    const statusClass = (status: string) =>
      status === "APPROVED"
        ? "text-green-700"
        : status === "REJECTED"
        ? "text-red-700"
        : status === "RECOMMEND_CHANGE"
        ? "text-violet-700"
        : "text-amber-700";
    const formatApprovalStatus = (status: string) =>
      status === "PENDING"
        ? "PENDING"
        : status === "RECOMMEND_CHANGE"
        ? "RECOMMENDED CHANGE"
        : stringFormat(status);
    const progressColumnDefs: ColDef[] = [
      {
        headerName: "GRANT ID",
        field: "grantIdentifier",
        minWidth: 150,
        rowGroup: approvalProgressGroupBy === "grantId",
        hide: approvalProgressGroupBy === "grantId",
      },
      { headerName: "EMPLOYEE", field: "employeeName", minWidth: 180 },
      {
        headerName: "STEP",
        field: "approvalStep",
        minWidth: 95,
        maxWidth: 110,
      },
      {
        headerName: "APPROVER",
        field: "approverName",
        minWidth: 180,
        cellRenderer: (params: any) => (
          <div>
            <div>{params.data?.approverName || "—"}</div>
            {params.data?.approverEmail && (
              <div className="mt-0.5 text-xs text-gray-light">
                {params.data.approverEmail}
              </div>
            )}
          </div>
        ),
      },
      {
        headerName: "APPROVAL STATUS",
        field: "status",
        minWidth: 250,
        rowGroup: approvalProgressGroupBy === "approvalStatus",
        hide: approvalProgressGroupBy === "approvalStatus",
        cellRenderer: (params: any) => (
          <span
            className={`font-medium whitespace-nowrap ${statusClass(
              params.value
            )}`}
          >
            {formatApprovalStatus(params.value)}
          </span>
        ),
      },
      {
        headerName: "OPTIONS / VALUE",
        field: "originalValue",
        minWidth: 230,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          if (params.value == null) return "—";
          return `Options: ${Number(
            params.data?.originalOptions || 0
          ).toLocaleString()} · Value: ${formatCurrency(
            Number(params.value),
            currency
          )}`;
        },
        cellRenderer: (params: any) => (
          <span className="text-xs text-primaryText">
            {params.valueFormatted || "—"}
          </span>
        ),
      },
      {
        headerName: "RECOMMENDED CHANGE",
        field: "proposedChanges",
        minWidth: 230,
        valueGetter: (params) =>
          formatRecommendation(params.data?.proposedChanges),
        cellRenderer: (params: any) => (
          <span className="text-xs text-primaryText">{params.value}</span>
        ),
      },
      {
        headerName: "NOTE",
        field: "note",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => params.data?.note || "—",
      },
      {
        headerName: "UPDATED",
        field: "respondedAt",
        minWidth: 135,
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(params.value) : "—",
      },
      {
        headerName: "",
        minWidth: 58,
        maxWidth: 66,
        pinned: "right",
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (params: any) => {
          const row = params.data;
          if (!row || row.status !== "RECOMMEND_CHANGE") return null;
          return (
            <RecommendationActionsMenu
              grantId={row.grantId}
              recommendation={{
                id: row.id,
                grantId: row.grantId,
                grantApprovalId: row.id,
                comment: row.note || "",
                proposedChanges: row.proposedChanges,
              }}
              onNavigate={onClose}
              onResolved={() =>
                approvalProgressQueries[row.queryIndex]?.refetch()
              }
            />
          );
        },
      },
    ];
    const progressAutoGroupColumnDef: ColDef = {
      headerName:
        approvalProgressGroupBy === "approvalStatus"
          ? "APPROVAL STATUS"
          : "GRANT ID",
      minWidth: 280,
      wrapText: false,
      autoHeight: false,
      cellRendererParams: {
        suppressCount: false,
        innerRenderer: (params: any) =>
          approvalProgressGroupBy === "approvalStatus"
            ? formatApprovalStatus(params.value)
            : params.value,
      },
    };
    const exportApprovalProgress = () => {
      if (!progressRows.length) return;

      const exportParams: ExcelExportParams = {
        fileName: `Approval_Progress_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
        sheetName: "Approval Progress",
        skipRowGroups: true,
        // Export every approval row for the selected grants even when the UI
        // is currently grouped with its groups collapsed.
        exportedRows: "all",
        columnKeys: [
          "grantIdentifier",
          "employeeName",
          "approvalStep",
          "approverName",
          "status",
          "originalValue",
          "proposedChanges",
          "note",
          "respondedAt",
        ],
        processHeaderCallback: (params: any) =>
          params.column.getColId() === "approverName"
            ? "APPROVER / MANAGER"
            : params.column.getColDef().headerName,
        processCellCallback: (params: any) => {
          const columnId = params.column.getColId();
          if (columnId === "status") return formatApprovalStatus(params.value);
          if (columnId === "originalValue") {
            return `Options: ${Number(
              params.node?.data?.originalOptions || 0
            ).toLocaleString()} · Value: ${formatCurrency(
              Number(params.value),
              currency
            )}`;
          }
          if (columnId === "respondedAt")
            return params.value ? formatDisplayDate(params.value) : "—";
          return params.value ?? "—";
        },
      };

      approvalProgressGridRef.current?.api.exportDataAsExcel(exportParams);
    };

    return (
      <div className="w-full">
        <div className="px-10 py-7 text-lg font-medium border-b flex items-center justify-between">
          <div>
            <h6>Approval progress</h6>
            <p className="mt-1 text-sm font-normal text-secondaryText">
              Review the approval stage for each selected grant.
            </p>
          </div>
          <button
            className="text-gray-light hover:text-primaryText"
            onClick={onClose}
          >
            <Icon icon="ri:close-line" height={22} />
          </button>
        </div>
        <VStack className="px-10 py-7 gap-5">
          {adminApprovalTabs}
          {selectedGrants.length === 0 ? (
            <InfoBox message="Select one or more grants to review their approval progress." />
          ) : progressLoading ? (
            <InfoBox message="Loading approval progress…" />
          ) : (
            <>
              <HStack className="gap-2 items-center">
                <span className="text-xs text-gray-500 font-medium">
                  Group by:
                </span>
                {[
                  { value: "none", label: "None" },
                  { value: "grantId", label: "Grant ID" },
                  { value: "approvalStatus", label: "Approval Status" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setApprovalProgressGroupBy(
                        option.value as typeof approvalProgressGroupBy
                      )
                    }
                    className={`text-xs px-3 py-1 rounded border transition-colors ${
                      approvalProgressGroupBy === option.value
                        ? "bg-orange-501 text-white border-orange-501"
                        : "bg-white text-gray-600 border-gray-300 hover:border-orange-501 hover:text-orange-501"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                <ButtonSecondary
                  className="ml-auto"
                  disabled={!progressRows.length}
                  onClick={exportApprovalProgress}
                >
                  Export Excel
                </ButtonSecondary>
              </HStack>
              <div className="w-full ag-theme-material rounded-lg border border-borderColor">
                <AgGridReact
                  ref={approvalProgressGridRef}
                  rowData={progressRows}
                  columnDefs={progressColumnDefs}
                  autoGroupColumnDef={progressAutoGroupColumnDef}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    wrapText: true,
                    autoHeight: true,
                    minWidth: 130,
                    headerClass: "approval-progress-header",
                    menuTabs: ["filterMenuTab"],
                  }}
                  excelStyles={[
                    {
                      id: "approval-progress-header",
                      font: {
                        bold: true,
                        color: "#FFFFFF",
                        fontName: "Calibri",
                      },
                      interior: { color: "#212EBF", pattern: "Solid" },
                      alignment: { vertical: "Center" },
                      borders: {
                        borderBottom: {
                          color: "#212EBF",
                          lineStyle: "Continuous",
                          weight: 2,
                        },
                      },
                    },
                  ]}
                  groupDefaultExpanded={0}
                  animateRows
                  suppressCellFocus
                  suppressAggFuncInHeader
                  getRowId={(params) => params.data.id}
                  domLayout="autoHeight"
                />
              </div>
            </>
          )}
        </VStack>
        <HStack className="justify-end px-10 py-6 border-t">
          <ButtonPrimary1 onClick={() => setAdminApprovalTab("actions")}>
            {isForApprovalState
              ? "Back to approval actions"
              : "Back to bulk update"}
          </ButtonPrimary1>
        </HStack>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="px-10 text-lg font-medium border-b py-7 flex items-center justify-between">
        <h6>Bulk Update Grant Status</h6>
        <button
          className="text-gray-light hover:text-primaryText"
          onClick={onClose}
        >
          <Icon icon="ri:close-line" height={22} />
        </button>
      </div>

      {canViewApprovalProgress && (
        <div className="px-10 pt-6">{adminApprovalTabs}</div>
      )}

      <VStack className="px-10 py-8 gap-8">
        {/* Summary cards */}
        <HStack className="gap-4">
          <div className="flex-1 border border-borderColor shadow-box bg-white rounded-lg p-5">
            <div className="text-xs text-gray-light mb-1">Number of Grants</div>
            <div className="text-2xl font-semibold text-primaryText">
              {grants.length}
            </div>
          </div>
          <div className="flex-1 border border-borderColor shadow-box bg-white rounded-lg p-5">
            <div className="text-xs text-gray-light mb-1">
              Current State of Grants
            </div>
            <div className="mt-1">
              <StatusLabel state={grants[0]?.optionHolderState} />
            </div>
          </div>
          <div className="flex-1 border border-borderColor shadow-box bg-white rounded-lg p-5">
            <div className="text-xs text-gray-light mb-1">Number of Plans</div>
            <div className="text-2xl font-semibold text-primaryText">
              {uniquePlanCount}
            </div>
          </div>
          {hasAnyAllocationMatrix && deviatingCount > 0 && (
            <div className="flex-1 border border-borderColor shadow-box bg-white rounded-lg p-5">
              <div className="text-xs text-gray-light mb-1">
                Grants Deviating from Allocation Matrix
              </div>
              <div className="text-2xl font-semibold text-primaryText">
                {deviatingCount}
              </div>
            </div>
          )}
        </HStack>

        {hasAnyAllocationMatrix && (
          <InfoBox
            message={`Allocated values and limit checks are calculated using the ${
              companyData?.companyMetaData.typeOfTheCompany === "PUBLIC"
                ? "latest share price"
                : "latest valuation"
            } as of today.`}
          />
        )}

        {/* AG Grid Table */}
        <div
          className="bulk-state-grid ag-theme-material border border-borderColor rounded-lg overflow-hidden shrink-0"
          style={
            isGridAutoHeight
              ? { width: "100%" }
              : {
                  height:
                    GRID_HEADER_HEIGHT +
                    GRID_MAX_VISIBLE_ROWS * GRID_ROW_HEIGHT +
                    2,
                  width: "100%",
                }
          }
        >
          <AgGridReact
            ref={gridRef}
            rowData={gridRows}
            columnDefs={getColumnDefs()}
            getRowId={(params) => params.data.optionHolderId}
            rowSelection="multiple"
            domLayout={isGridAutoHeight ? "autoHeight" : "normal"}
            headerHeight={44}
            suppressRowClickSelection
            rowClass="border-b border-borderColor"
            onSelectionChanged={() => {
              const selected = gridRef.current?.api?.getSelectedRows() || [];
              setSelectedGrants(selected);
            }}
            onGridReady={(params) => {
              params.api.selectAll();
            }}
            defaultColDef={
              {
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: false,
                filter: true,
                menuTabs: ["filterMenuTab"],
              } as any
            }
            sideBar={sideBar}
            suppressMenuHide={true}
            gridOptions={
              {
                suppressColumnsToolPanel: true,
              } as any
            }
          />
        </div>
        <VStack className="gap-2 w-1/2">
          <Label>
            Note
            {availableStates.includes("WITHDRAW") && (
              <span className="text-red-500 ml-0.5">*</span>
            )}
          </Label>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Error text={error} />
          {availableStates.includes("WITHDRAW") && (
            <HStack className="gap-8 mt-2 text-sm font-normal">
              <SwitchButton
                value={lapseWithdrawed}
                label="Lapsed Withdrawed options?"
                onClick={() => setLapseWithdrawed(!lapseWithdrawed)}
              />
            </HStack>
          )}
        </VStack>
        {canOffer && (
          <VStack>
            {generateCredentialsOnGrantOffer && (
              <div className="mb-2 w-fit">
                <InfoBox
                  variant="warning"
                  message="Moving grants to Offered will generate employee portal credentials for employees who do not already have login credentials."
                />
              </div>
            )}
            <div className="mb-5">
              <SwitchButton
                value={overrideAcceptanceWindow}
                label="Override default acceptance window"
                onClick={() =>
                  setOverrideAcceptanceWindow(!overrideAcceptanceWindow)
                }
              />
              {overrideAcceptanceWindow && (
                <div className="mt-3 w-72">
                  <Label className="required">Acceptance window (days)</Label>
                  <Input
                    className="mt-2"
                    type="number"
                    min="0"
                    step="1"
                    value={acceptanceNumber}
                    onChange={(event) =>
                      setAcceptanceNumber(event.target.value)
                    }
                  />
                  <p className="mt-1 text-xs text-secondaryText">
                    Applies to every selected grant. Set to 0 to keep the offer
                    open.
                  </p>
                </div>
              )}
            </div>
            <HStack className="gap-8 text-sm font-normal">
              <div className="flex gap-2">
                <div className="flex items-center w-18">
                  <SwitchButton
                    value={emailCheck}
                    label=""
                    onClick={() => setEmailCheck(!emailCheck)}
                  />
                </div>
                <div className="flex flex-col">
                  <div className="font-medium">
                    {canOffer
                      ? "Send offer email to employees"
                      : "Send email to employees"}
                  </div>
                  <div className="text-secondaryText text-xs">
                    {isReissue
                      ? "Notify the employees about this reissued grant"
                      : "Notify the employees about this grant"}
                  </div>
                </div>
              </div>
            </HStack>
            <HStack className="w-3/4 mt-2">
              {!siteOfferedEmailsEnabled && emailCheck && (
                <InfoBox
                  message="Employee emails for offered grants are currently
                          disabled in site settings. Enabling this toggle will
                          override that setting and send an email for the selected grant(s)."
                />
              )}
              {siteOfferedEmailsEnabled && !emailCheck && (
                <InfoBox
                  message="Employee emails for offered grants are currently enabled
                        in site settings. Disabling this toggle will override
                        that setting and not send the email."
                />
              )}
            </HStack>
          </VStack>
        )}
      </VStack>

      {hasMixedApprovalSelection && !isAdministrator && (
        <div className="mx-10 mb-4">
          <InfoBox
            variant="warning"
            message="This selection contains grants with different next actions. Bulk actions are enabled only when every selected grant can receive the same action. Use ‘My action required’ to select only grants waiting for your sign-off."
          />
        </div>
      )}
      {isForApprovalState && isAdministrator && selectedGrants.length > 0 && (
        <div className="mx-10 mb-4 rounded-lg border border-borderColor bg-white p-4">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-sm font-medium text-primaryText">
                Final approval decision for {selectedGrants.length} grant
                {selectedGrants.length === 1 ? "" : "s"}
              </div>
              <p className="mt-1 text-xs text-secondaryText">
                {adminPendingSignOffGrants.length > 0
                  ? `${adminPendingSignOffGrants.length} grant${
                      adminPendingSignOffGrants.length === 1 ? " is" : "s are"
                    } still awaiting sign-off. Approval will require an override confirmation.`
                  : "All required sign-offs are complete for the selected grants."}
              </p>
            </div>
            <HStack className="shrink-0 gap-2">
              <ButtonPrimary1
                onClick={() =>
                  executeStateChange("NOT_APPROVED", selectedGrants)
                }
                loading={
                  currentLoadingState === "NOT_APPROVED" &&
                  isStateChanged === "loading"
                }
              >
                Not approve ({selectedGrants.length})
              </ButtonPrimary1>
              <ButtonPrimary
                onClick={() =>
                  adminPendingSignOffGrants.length > 0
                    ? setAdminOverrideWarningOpen(true)
                    : executeStateChange("APPROVED", selectedGrants)
                }
                loading={
                  currentLoadingState === "APPROVED" &&
                  isStateChanged === "loading"
                }
              >
                Approve grants ({selectedGrants.length})
              </ButtonPrimary>
            </HStack>
          </div>
        </div>
      )}
      {isForApprovalState &&
        !isAdministrator &&
        (ownSignOffTargets.length > 0 || directActionTargets.length > 0) && (
          <div className="mx-10 mb-4 space-y-3">
            {ownSignOffTargets.length > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-borderColor bg-white p-4">
                <div>
                  <div className="text-sm font-medium text-primaryText">
                    Your sign-off is due for {ownSignOffTargets.length} grant
                    {ownSignOffTargets.length === 1 ? "" : "s"}
                  </div>
                  <p className="mt-1 text-xs text-secondaryText">
                    This records your approver decision; it does not make the
                    final admin decision.
                  </p>
                </div>
                <HStack className="gap-2">
                  <ButtonPrimary1
                    disabled={!allSelectedCanSignOff}
                    title={
                      allSelectedCanSignOff ? undefined : signOffDisabledReason
                    }
                    loading={signOffActionLoading === SignOffDecision.REJECTED}
                    onClick={() =>
                      handleActionableBulkSignOff(SignOffDecision.REJECTED)
                    }
                  >
                    Reject sign-off ({selectedGrants.length})
                  </ButtonPrimary1>
                  <ButtonPrimary
                    disabled={!allSelectedCanSignOff}
                    title={
                      allSelectedCanSignOff ? undefined : signOffDisabledReason
                    }
                    loading={signOffActionLoading === SignOffDecision.APPROVED}
                    onClick={() =>
                      handleActionableBulkSignOff(SignOffDecision.APPROVED)
                    }
                  >
                    Approve sign-off ({selectedGrants.length})
                  </ButtonPrimary>
                </HStack>
              </div>
            )}
            {directActionTargets.length > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-borderColor bg-white p-4">
                <div>
                  <div className="text-sm font-medium text-primaryText">
                    Final admin decision is ready for{" "}
                    {directActionTargets.length} grant
                    {directActionTargets.length === 1 ? "" : "s"}
                  </div>
                  <p className="mt-1 text-xs text-secondaryText">
                    All required sign-offs are complete for these grants.
                  </p>
                </div>
                <HStack className="gap-2">
                  <ButtonPrimary1
                    disabled={!allSelectedCanReceiveAdminDecision}
                    title={
                      allSelectedCanReceiveAdminDecision
                        ? undefined
                        : adminDecisionDisabledReason
                    }
                    onClick={() =>
                      executeStateChange("NOT_APPROVED", directActionTargets)
                    }
                    loading={
                      currentLoadingState === "NOT_APPROVED" &&
                      isStateChanged === "loading"
                    }
                  >
                    Not approve ({selectedGrants.length})
                  </ButtonPrimary1>
                  <ButtonPrimary
                    disabled={!allSelectedCanReceiveAdminDecision}
                    title={
                      allSelectedCanReceiveAdminDecision
                        ? undefined
                        : adminDecisionDisabledReason
                    }
                    onClick={() =>
                      executeStateChange("APPROVED", directActionTargets)
                    }
                    loading={
                      currentLoadingState === "APPROVED" &&
                      isStateChanged === "loading"
                    }
                  >
                    Approve grants ({selectedGrants.length})
                  </ButtonPrimary>
                </HStack>
              </div>
            )}
          </div>
        )}
      {isForApprovalState && !isAdministrator && awaitingOtherSignOffCount > 0 && (
        <div className="mx-10 mb-4">
          <InfoBox
            message={`${awaitingOtherSignOffCount} selected grant${
              awaitingOtherSignOffCount === 1 ? " is" : "s are"
            } awaiting another approver. No action is available for those grants yet.`}
          />
        </div>
      )}

      {/* Footer */}
      <HStack className="flex-row-reverse justify-start gap-4 px-10 py-6 border-t">
        {[...availableStates]
          .filter(
            (state) =>
              !isForApprovalState ||
              (state !== "APPROVED" && state !== "NOT_APPROVED")
          )
          .map((state, index) =>
            index === 0 ? (
              <ButtonPrimary
                key={state}
                onClick={() => executeStateChange(state, directActionTargets)}
                disabled={directActionTargets.length === 0}
                loading={
                  currentLoadingState === state && isStateChanged === "loading"
                }
              >
                {stringFormat(state)}
              </ButtonPrimary>
            ) : (
              <ButtonSecondary
                key={state}
                onClick={() => executeStateChange(state, directActionTargets)}
                disabled={
                  directActionTargets.length === 0 ||
                  (currentLoadingState === state &&
                    isStateChanged === "loading")
                }
              >
                {currentLoadingState === state && isStateChanged === "loading"
                  ? "Please wait..."
                  : stringFormat(state)}
              </ButtonSecondary>
            )
          )}
      </HStack>
    </div>
  );
}
export default BulkOptionStateChange;
